export type CompanyProvisioningField =
  | "nome"
  | "indirizzo"
  | "citta"
  | "cap"
  | "partita_iva"
  | "codice_fiscale"
  | "telefono"
  | "email"
  | "suffix"
  | "slug";

export type PrimoUpClinicSource = {
  nome: string;
  indirizzo: string;
  citta: string;
  cap: string;
  partita_iva?: string;
  codice_fiscale?: string;
  telefono?: string;
  email?: string;
  suffix: string;
  slug: string;
};

export type PrimoUpClinicPayload = {
  slug: string;
  name: string;
  address: string;
  city: string;
  zip: string;
  vat: string;
  tax_code: string;
  phone: string;
  email: string;
  prescription_suffix: string;
};

const CLINIC_ERROR_FIELD_MAP: Record<string, CompanyProvisioningField> = {
  name: "nome",
  address: "indirizzo",
  city: "citta",
  zip: "cap",
  vat: "partita_iva",
  tax_code: "codice_fiscale",
  phone: "telefono",
  email: "email",
  prescription_suffix: "suffix",
  slug: "slug",
};

const MAX_API_MESSAGE_LENGTH = 320;

function safeApiMessage(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const message = value.trim().replace(/\s+/g, " ");
  if (!message || message.length > MAX_API_MESSAGE_LENGTH) return null;
  return message;
}

function firstApiError(value: unknown): string | null {
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = safeApiMessage(item);
      if (message) return message;
    }
    return null;
  }
  return safeApiMessage(value);
}

export function buildPrimoUpClinicPayload(
  source: PrimoUpClinicSource
): PrimoUpClinicPayload {
  return {
    slug: source.slug.trim(),
    name: source.nome.trim(),
    address: source.indirizzo.trim(),
    city: source.citta.trim(),
    zip: source.cap.trim(),
    vat: source.partita_iva?.trim() ?? "",
    tax_code: source.codice_fiscale?.trim().toUpperCase() ?? "",
    phone: source.telefono?.trim() ?? "",
    email: source.email?.trim().toLowerCase() ?? "",
    prescription_suffix: source.suffix.trim().toUpperCase(),
  };
}

export function parsePrimoUpClinicValidationError(body: unknown): {
  message: string;
  fieldErrors: Partial<Record<CompanyProvisioningField, string>>;
} {
  const fallback = "PrimoUp non ha accettato i dati della società.";
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return { message: fallback, fieldErrors: {} };
  }

  const record = body as Record<string, unknown>;
  const fieldErrors: Partial<Record<CompanyProvisioningField, string>> = {};
  if (record.errors && typeof record.errors === "object" && !Array.isArray(record.errors)) {
    for (const [apiField, value] of Object.entries(
      record.errors as Record<string, unknown>
    )) {
      const formField = CLINIC_ERROR_FIELD_MAP[apiField];
      const message = firstApiError(value);
      if (formField && message) fieldErrors[formField] = message;
    }
  }

  return {
    message: safeApiMessage(record.message) ?? fallback,
    fieldErrors,
  };
}

export const PRIMOUP_ATTACHMENT_ACCEPT = ".jpg,.jpeg,.png";
export const PRIMOUP_ATTACHMENT_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
] as const;

export function getPrimoUpAttachmentValidationError(args: {
  filename: string;
  contentType: string;
}): string | null {
  const filename = args.filename.trim();
  const contentType = args.contentType.trim().toLowerCase();
  if (!/\.(?:jpe?g|png)$/i.test(filename)) {
    return "Sono ammessi soltanto file JPG, JPEG o PNG.";
  }
  if (!(PRIMOUP_ATTACHMENT_CONTENT_TYPES as readonly string[]).includes(contentType)) {
    return "Il file selezionato non è un’immagine JPG o PNG valida.";
  }
  return null;
}

export function getPrimoUpApiErrorMessage(
  body: unknown,
  fallback: string
): string {
  if (!body || typeof body !== "object" || Array.isArray(body)) return fallback;
  const record = body as Record<string, unknown>;
  const messages: string[] = [];
  if (record.errors && typeof record.errors === "object" && !Array.isArray(record.errors)) {
    for (const value of Object.values(record.errors as Record<string, unknown>)) {
      const message = firstApiError(value);
      if (message && !messages.includes(message)) messages.push(message);
    }
  }
  return messages.join(" ") || safeApiMessage(record.message) || fallback;
}
